import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { friendlyError } from '../lib/errors.js'
import { Card, CardBody, CardHeader } from './Card.js'
import { Button } from './Button.js'
import { Dialog } from './Dialog.js'
import { Field, Input } from './Field.js'
import { Empty } from './Empty.js'
import { UrgencyBadge } from './UrgencyBadge.js'

interface StudentExam {
  id: string
  title: string
  startDate: string
  endDate: string | null
  notes: string | null
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const todayIso = () => new Date().toISOString().slice(0, 10)

function daysFromToday(dateStr: string): number {
  const a = Date.parse(`${dateStr}T00:00:00Z`)
  const b = Date.parse(`${todayIso()}T00:00:00Z`)
  return Math.round((a - b) / 86_400_000)
}

/**
 * A student's SCHOOL exam dates (outside-academy). Admin adds/edits/removes dated
 * entries here; the class-session page surfaces upcoming ones for a class roster.
 */
export function StudentExamsPanel({ studentId }: { studentId: string }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<StudentExam | 'new' | null>(null)

  const examsQuery = useQuery({
    queryKey: ['student-exams', studentId],
    queryFn: async () => {
      const res = await api<StudentExam[]>(`/api/v1/student-exams?studentId=${studentId}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await api(`/api/v1/student-exams/${id}`, { method: 'DELETE' })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['student-exams', studentId] }),
    onError: (err) => alert(err instanceof Error ? err.message : 'Could not remove exam'),
  })

  const exams = examsQuery.data ?? []

  return (
    <Card>
      <CardHeader
        title="School exams"
        description="Dates of this student's school exams. Upcoming ones show on the class session."
        actions={
          <Button variant="secondary" size="sm" onClick={() => setEditing('new')}>
            Add exam
          </Button>
        }
      />
      <CardBody>
        {examsQuery.isLoading ? (
          <p className="text-sm text-fg-subtle">Loading…</p>
        ) : exams.length === 0 ? (
          <Empty
            icon="calendar"
            title="No school exams yet"
            description="Add exam dates so they show up on the class session."
          />
        ) : (
          <ul className="divide-y divide-border">
            {exams.map((ex) => {
              const d = daysFromToday(ex.startDate)
              const upcoming = d >= 0
              return (
                <li key={ex.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <span className="font-medium text-fg">{ex.title}</span>
                      {upcoming ? <UrgencyBadge daysUntil={d} /> : null}
                    </span>
                    <span className="mt-0.5 block text-xs text-fg-subtle">
                      {fmtDate(ex.startDate)}
                      {ex.endDate ? ` → ${fmtDate(ex.endDate)}` : ''}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 whitespace-nowrap text-xs">
                    <button onClick={() => setEditing(ex)} className="text-fg-muted hover:text-fg">
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Remove "${ex.title}"?`)) remove.mutate(ex.id)
                      }}
                      className="text-fg-muted hover:text-danger"
                    >
                      Remove
                    </button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </CardBody>

      <ExamDialog
        studentId={studentId}
        exam={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['student-exams', studentId] })
          setEditing(null)
        }}
      />
    </Card>
  )
}

function ExamDialog({
  studentId,
  exam,
  onClose,
  onSaved,
}: {
  studentId: string
  exam: StudentExam | 'new' | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = exam !== null && exam !== 'new'
  const [title, setTitle] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (exam === 'new') {
      setTitle('')
      setStartDate(todayIso())
      setEndDate('')
    } else if (exam) {
      setTitle(exam.title)
      setStartDate(exam.startDate)
      setEndDate(exam.endDate ?? '')
    }
    setError(null)
  }, [exam])

  const invalid = !title.trim() || !startDate || (!!endDate && endDate < startDate)

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title: title.trim(),
        startDate,
        endDate: endDate || undefined,
      }
      const res = isEdit
        ? await api(`/api/v1/student-exams/${(exam as StudentExam).id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : await api(`/api/v1/student-exams`, {
            method: 'POST',
            body: JSON.stringify({ studentId, ...body }),
          })
      if ('error' in res) throw new Error(friendlyError(res.error))
      return res.data
    },
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save exam'),
  })

  return (
    <Dialog
      open={exam !== null}
      onClose={onClose}
      title={isEdit ? 'Edit school exam' : 'Add school exam'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={invalid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Half-yearly exams"
            autoFocus
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start date" required>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="End date" optional>
            <Input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
        </div>
        {endDate && endDate < startDate ? (
          <p className="text-xs text-danger">End date must be on or after the start date.</p>
        ) : null}
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </div>
    </Dialog>
  )
}
